<script setup lang="ts">
/**
 * The public signing page: one person, one link, one document.
 *
 * Rendered by App.vue ahead of the Lark gate (see useSigningLink.ts). Mobile
 * first — most people open a signing link on a phone.
 *
 * The document is shown by the browser's own PDF viewer, not pdf.js. On a
 * desktop that is an <iframe>. Phones are where native embedding fails: Chrome
 * on Android shows nothing inline and Safari on iOS shows only the first page
 * of an embedded PDF. So on a touch screen the page offers "Open document"
 * instead, which hands the file to the phone's own full-screen viewer — every
 * page, zoom included — and the signer comes back to this tab to sign. That is
 * a tap more than an inline viewer, against a megabyte of pdf.js for every
 * signer; the download link is there on every device besides.
 */
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertCircle, CheckCircle2, Clock, ExternalLink, FileText, Lock, PenLine, Type, XCircle, Download } from 'lucide-vue-next'
import {
  SIGNATURE_NAME_MAX_LENGTH,
  SIGNATURE_REASON_MAX_LENGTH,
  signatureErrorMessageKey,
  type SigningLinkInfo,
  type SigningLinkState,
} from '@lark-automadoc/shared-types'
import LanguageSelector from '@/components/LanguageSelector.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import SignaturePad from '@/components/SignaturePad.vue'
import { SigningApiError, signingApi } from '@/composables/useSigningLink'

const props = defineProps<{ token: string }>()
const { t, locale } = useI18n()
const api = signingApi(props.token)

/** Where the page is: the link's own state, or what the signer just did here. */
type View = 'loading' | 'invalid' | SigningLinkState | 'done' | 'declinedHere'
const view = ref<View>('loading')
const info = ref<SigningLinkInfo | null>(null)
const session = ref<string | null>(null)
const error = ref('')

const password = ref('')
const unlocking = ref(false)

const mode = ref<'draw' | 'type'>('draw')
const fullName = ref('')
const consent = ref(false)
const hasInk = ref(false)
const signing = ref(false)
const pad = ref<InstanceType<typeof SignaturePad> | null>(null)
const result = ref<{ signedCount: number; totalSigners: number; downloadUrl: string | null; completed: boolean } | null>(null)

const declining = ref(false)
const declineReason = ref('')
const declineBusy = ref(false)

/**
 * Inline viewing only where the browser can show a whole PDF in a frame. A
 * coarse pointer is the honest test for "phone or tablet" here: those are the
 * browsers that cannot, whatever navigator.pdfViewerEnabled claims.
 */
const inlineViewer = computed(() => {
  const nav = navigator as Navigator & { pdfViewerEnabled?: boolean }
  return nav.pdfViewerEnabled !== false && !window.matchMedia('(pointer: coarse)').matches
})

const documentUrl = computed(() => api.documentUrl(session.value))
const downloadUrl = computed(() => api.documentUrl(session.value, true))
const canSign = computed(() => hasInk.value && fullName.value.trim().length >= 2 && consent.value && !signing.value)
const expiresOn = computed(() => (info.value?.expiresAt ? new Date(info.value.expiresAt).toLocaleDateString(locale.value) : ''))

function messageFor(err: unknown): string {
  return t(signatureErrorMessageKey(err instanceof SigningApiError ? err.code : undefined))
}

async function load(): Promise<void> {
  error.value = ''
  try {
    info.value = await api.info(session.value)
    view.value = info.value.state
    if (!fullName.value && info.value.signerName) fullName.value = info.value.signerName
  } catch (err) {
    if (err instanceof SigningApiError && err.status === 404) view.value = 'invalid'
    else {
      error.value = messageFor(err)
      if (view.value === 'loading') view.value = 'invalid'
    }
  }
}

async function unlock(): Promise<void> {
  if (!password.value || unlocking.value) return
  unlocking.value = true
  error.value = ''
  try {
    session.value = (await api.unlock(password.value)).session
    password.value = ''
    await load()
  } catch (err) {
    if (err instanceof SigningApiError && err.code === 'SIGN_BLOCKED') view.value = 'blocked'
    else if (err instanceof SigningApiError && err.code === 'SIGN_WRONG_PASSWORD' && info.value) {
      info.value = { ...info.value, attemptsLeft: Number(err.body.attemptsLeft ?? 0) }
      error.value = messageFor(err)
    } else error.value = messageFor(err)
  } finally {
    unlocking.value = false
  }
}

async function sign(): Promise<void> {
  const signaturePng = pad.value?.toDataUrl()
  if (!signaturePng || !canSign.value) return
  signing.value = true
  error.value = ''
  try {
    const response = await api.sign({ typedName: fullName.value.trim(), signaturePng, consent: true, session: session.value ?? undefined })
    result.value = {
      signedCount: response.signedCount,
      totalSigners: response.totalSigners,
      downloadUrl: response.downloadUrl,
      completed: response.state === 'completed',
    }
    view.value = 'done'
  } catch (err) {
    error.value = messageFor(err)
    // The link may have closed or been used meanwhile, or the unlocked session
    // run out (401): show what it is now — the password form, if that is it.
    if (err instanceof SigningApiError && [401, 409, 410, 423].includes(err.status)) await load()
  } finally {
    signing.value = false
  }
}

async function decline(): Promise<void> {
  declineBusy.value = true
  error.value = ''
  try {
    await api.decline({ reason: declineReason.value.trim() || undefined, session: session.value ?? undefined })
    view.value = 'declinedHere'
  } catch (err) {
    error.value = messageFor(err)
    if (err instanceof SigningApiError && [401, 409, 410, 423].includes(err.status)) await load()
  } finally {
    declineBusy.value = false
  }
}

const closedMessage = computed(() => {
  switch (view.value) {
    case 'completed':
      return t('signStateCompleted')
    case 'declined':
      return t('signStateDeclined')
    case 'expired':
      return t('signStateExpired')
    case 'cancelled':
      return t('signStateCancelled')
    default:
      return ''
  }
})

onMounted(() => {
  document.title = t('signPageTitle')
  void load()
})
</script>

<template>
  <div class="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
    <header class="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div class="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
        <img src="/logo.svg" alt="AutomaDoc" class="h-8 w-8" />
        <span class="text-lg font-bold text-gray-800 dark:text-gray-100 flex-1">AutomaDoc</span>
        <LanguageSelector />
      </div>
    </header>

    <main class="flex-1 w-full max-w-3xl mx-auto px-4 py-6">
      <LoadingSpinner v-if="view === 'loading'" />

      <!-- The link is not one of ours, or no longer is. -->
      <section v-else-if="view === 'invalid'" class="text-center py-8">
        <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><AlertCircle :size="20" class="shrink-0 text-orange-500" aria-hidden="true" />{{ t('signInvalidTitle') }}</h1>
        <p class="text-gray-600 dark:text-gray-400">{{ error || t('signInvalidLink') }}</p>
      </section>

      <!-- Closed: say which, and nothing else. -->
      <section v-else-if="closedMessage" class="text-center py-8">
        <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><FileText :size="20" class="shrink-0 text-gray-400" aria-hidden="true" />{{ closedMessage }}</h1>
        <p v-if="view !== 'completed'" class="text-gray-600 dark:text-gray-400">{{ t('signStateClosedHint') }}</p>
      </section>

      <section v-else-if="view === 'blocked'" class="text-center py-8">
        <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><Lock :size="20" class="shrink-0 text-red-500" aria-hidden="true" />{{ t('signStateBlocked') }}</h1>
      </section>

      <section v-else-if="view === 'declinedHere'" class="text-center py-8">
        <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><XCircle :size="20" class="shrink-0 text-gray-500" aria-hidden="true" />{{ t('signDeclinedTitle') }}</h1>
        <p class="text-gray-600 dark:text-gray-400">{{ t('signDeclinedBody') }}</p>
      </section>

      <!-- Just signed. -->
      <section v-else-if="view === 'done' && result" class="text-center py-8">
        <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><CheckCircle2 :size="20" class="shrink-0 text-green-600" aria-hidden="true" />{{ t('signSignedTitle') }}</h1>
        <p class="text-gray-600 dark:text-gray-400 mb-6">
          {{ result.completed ? t('signCompletedBody') : t('signSignedBody', { signed: result.signedCount, total: result.totalSigners }) }}
        </p>
        <a
          v-if="result.downloadUrl"
          :href="result.downloadUrl"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
        >
          <Download :size="16" /> {{ t('signDownloadCopy') }}
        </a>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">{{ t('signCopyPreparing') }}</p>
      </section>

      <template v-else-if="info">
        <!-- Behind a password the API reveals no title, company or role. -->
        <section v-if="info.title" class="mb-5">
          <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100 break-words">{{ info.title }}</h1>
          <p class="mt-1 text-gray-600 dark:text-gray-400">
            {{ info.companyName ? t('signRequestedBy', { company: info.companyName, role: info.role }) : t('signRequestedByUnknown', { role: info.role }) }}
          </p>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ t('signProgress', { signed: info.signedCount, total: info.totalSigners }) }}
            <template v-if="expiresOn"> · {{ t('signExpiresOn', { date: expiresOn }) }}</template>
          </p>
        </section>

        <!-- Password -->
        <section v-if="view === 'locked'" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <div class="flex items-center gap-2 mb-2">
            <Lock :size="18" class="text-gray-500" />
            <h2 class="font-semibold text-gray-900 dark:text-gray-100">{{ t('signPasswordTitle') }}</h2>
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">{{ t('signPasswordHint') }}</p>
          <form class="flex flex-col sm:flex-row gap-2" @submit.prevent="unlock">
            <label class="sr-only" for="sign-password">{{ t('signPasswordLabel') }}</label>
            <input
              id="sign-password"
              v-model="password"
              type="password"
              autocomplete="off"
              :placeholder="t('signPasswordLabel')"
              class="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
            <button
              type="submit"
              :disabled="!password || unlocking"
              class="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {{ t('signPasswordSubmit') }}
            </button>
          </form>
          <p v-if="info.attemptsLeft !== undefined" class="mt-2 text-xs text-gray-500">{{ t('signAttemptsLeft', { count: info.attemptsLeft }, info.attemptsLeft) }}</p>
        </section>

        <!-- Someone else signs first -->
        <section v-else-if="view === 'waiting'" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-center">
          <Clock :size="36" class="mx-auto mb-3 text-blue-500" />
          <p class="text-gray-700 dark:text-gray-300 mb-4">{{ t('signStateWaiting', { count: info.waitingFor ?? 1 }) }}</p>
          <button class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200" @click="load">
            {{ t('signRefresh') }}
          </button>
        </section>

        <template v-else>
          <!-- The document -->
          <section class="mb-6">
            <div v-if="inlineViewer" class="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white">
              <iframe :src="documentUrl" :title="info.title" class="w-full h-[70vh]" />
            </div>
            <div v-else class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 text-center">
              <FileText :size="36" class="mx-auto mb-2 text-gray-400" />
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">{{ t('signDocumentMobileHint') }}</p>
              <a
                :href="documentUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg"
              >
                <ExternalLink :size="16" /> {{ t('signOpenDocument') }}
              </a>
            </div>
            <div class="mt-2 flex justify-end">
              <a :href="downloadUrl" class="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                <Download :size="14" /> {{ t('signDownloadDocument') }}
              </a>
            </div>
          </section>

          <!-- Already signed: they may re-read it, nothing more. -->
          <section v-if="view === 'signed'" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3">
            <p class="flex items-center gap-2 text-sm text-green-800 dark:text-green-300"><CheckCircle2 :size="16" class="shrink-0 text-green-600" aria-hidden="true" />{{ t('signStateSignedAlready') }}</p>
          </section>

          <!-- Sign -->
          <section v-else class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
            <div class="flex items-center justify-between gap-2">
              <h2 class="font-semibold text-gray-900 dark:text-gray-100">{{ t('signYourSignature') }}</h2>
              <div class="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm" role="tablist">
                <button
                  role="tab"
                  :aria-selected="mode === 'draw'"
                  :class="['px-3 py-1.5 inline-flex items-center gap-1', mode === 'draw' ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-200']"
                  @click="mode = 'draw'"
                >
                  <PenLine :size="14" /> {{ t('signModeDraw') }}
                </button>
                <button
                  role="tab"
                  :aria-selected="mode === 'type'"
                  :class="['px-3 py-1.5 inline-flex items-center gap-1', mode === 'type' ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-200']"
                  @click="mode = 'type'"
                >
                  <Type :size="14" /> {{ t('signModeType') }}
                </button>
              </div>
            </div>

            <div>
              <label for="sign-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ t('signFullName') }}</label>
              <input
                id="sign-name"
                v-model="fullName"
                :maxlength="SIGNATURE_NAME_MAX_LENGTH"
                autocomplete="name"
                :placeholder="t('signFullNamePlaceholder')"
                class="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>

            <SignaturePad ref="pad" :mode="mode" :typed-text="fullName" @change="hasInk = $event" />

            <label class="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
              <input v-model="consent" type="checkbox" class="mt-1 h-4 w-4 shrink-0" />
              <span>{{ t('signConsent') }}</span>
            </label>

            <p v-if="error" class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>

            <div class="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
              <button
                type="button"
                class="px-4 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                @click="declining = !declining"
              >
                {{ t('signDecline') }}
              </button>
              <button
                type="button"
                :disabled="!canSign"
                class="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
                @click="sign"
              >
                {{ signing ? t('signSigning') : t('signButton') }}
              </button>
            </div>

            <div v-if="declining" class="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
              <h3 class="font-medium text-gray-900 dark:text-gray-100">{{ t('signDeclineTitle') }}</h3>
              <textarea
                v-model="declineReason"
                rows="3"
                :maxlength="SIGNATURE_REASON_MAX_LENGTH"
                :placeholder="t('signDeclineReason')"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
              />
              <div class="flex justify-end gap-2">
                <button type="button" class="px-4 py-2 text-sm text-gray-600 dark:text-gray-300" @click="declining = false">{{ t('cancel') }}</button>
                <button
                  type="button"
                  :disabled="declineBusy"
                  class="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  @click="decline"
                >
                  {{ t('signDeclineConfirm') }}
                </button>
              </div>
            </div>
          </section>
        </template>
        <p v-if="error && (view === 'locked' || view === 'waiting')" class="mt-3 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
      </template>
    </main>

    <footer class="py-6 text-center text-xs text-gray-400 dark:text-gray-500 px-4">{{ t('signFooterNote') }}</footer>
  </div>
</template>
